import { useDispatch, useSelector } from 'react-redux';
import { Group, GroupItem, IFlagsConfig } from '@/models';
import { selectGroups } from '@/store/selectors';
import { setGroup, removeGroup } from '@/store/reducers';
import { apiUser } from '@/utils/api/apiUser';
import { RootState } from '@/store/store';
import { isUserAnonymous } from '@/utils/helpers/user';
import { resolveGroupToSave } from '@/utils/helpers/groups';
import { useExtension } from '../integrations/useExtension.hook';

const HTTP_STATUS_NOT_FOUND = 404;

/**
 * Hook for managing user configuration updates with automatic extension notification
 * for anonymous users.
 *
 * This hook wraps updateGroupItems and updateFlagConfig to automatically notify
 * the extension when config changes occur for anonymous users, since they don't
 * have Firestore sync.
 */
export const useUserConfig = () => {
  const dispatch = useDispatch();
  const existingGroups = useSelector(selectGroups);
  const extensionHook = useExtension();
  const user = useSelector((state: RootState) => state.user);
  const isLoggedIn = (): boolean => !isUserAnonymous(user);

  const notifyExtensionWithoutBlocking = (): void => {
    if (isLoggedIn()) return;
    extensionHook.notifyExtensionConfigChanged();
  };

  const notifyExtensionAndWait = async (): Promise<void> => {
    if (isLoggedIn()) return;
    await extensionHook.notifyExtensionConfigChanged();
  };

  /**
   * Updates group items on the server and notifies extension for anonymous users.
   *
   * @param groupId - The ID of the group to update
   * @param items - The updated list of group items
   */
  const updateGroupItems = async (
    groupId: string,
    items: GroupItem[]
  ): Promise<void> => {
    await apiUser.updateGroupItems(groupId, items);

    await notifyExtensionAndWait();
  };

  /**
   * Updates flag configuration on the server and notifies extension for anonymous users.
   *
   * IMPORTANT: For logged-in users, changes sync automatically to the extension via Firestore.
   * For anonymous users, there is no cross-device sync (by design), so we automatically
   * notify the extension to fetch the updated config from the server.
   *
   * @param updateFlags - Partial flags configuration to update
   */
  const updateFlagConfig = async (
    updateFlags: Partial<IFlagsConfig>
  ): Promise<void> => {
    await apiUser.updateFlagConfig(updateFlags);

    await notifyExtensionAndWait();
  };

  /**
   * Saves a new group through the single-group endpoints so a stale local list can never
   * overwrite groups created on another device. An empty placeholder of the same type is
   * reused via PATCH; otherwise the group is created via POST.
   */
  const saveNewGroup = async (newGroup: Group): Promise<void> => {
    const { group, reusesPlaceholder } = resolveGroupToSave(
      newGroup,
      existingGroups
    );

    if (reusesPlaceholder) {
      const isSaved = await tryReusePlaceholder(group);
      if (isSaved) return;
    }

    await createGroup(newGroup);
  };

  const createGroup = async (group: Group): Promise<void> => {
    await apiUser.createGroup(group);
    dispatch(setGroup(group));

    notifyExtensionWithoutBlocking();
  };

  // a missing placeholder means the local list is out of sync with the server,
  // so drop it and let the caller create the group instead
  const tryReusePlaceholder = async (group: Group): Promise<boolean> => {
    try {
      await updateGroup(group);
      return true;
    } catch (error) {
      if ((error as { status?: number })?.status !== HTTP_STATUS_NOT_FOUND) {
        throw error;
      }
      dispatch(removeGroup(group.id));
      return false;
    }
  };

  /**
   * Updates an existing group's fields (schedule, items, name, icon, enabled, etc) via the
   * dedicated single-group endpoint (`PATCH /user-config/group/:id`) - never sends the whole
   * groups array, per the cross-repo sync rules. `id`/`type` are immutable on the server and are
   * stripped before sending.
   */
  const updateGroup = async (updatedGroup: Group): Promise<void> => {
    const { id, type, ...groupFields } = updatedGroup;
    await apiUser.updateGroup(id, groupFields);
    dispatch(setGroup(updatedGroup));

    notifyExtensionWithoutBlocking();
  };

  /**
   * Permanently removes a group via `DELETE /user-config/group/:id`. Note: if this was the
   * user's last group of a required type (schedule/focusTime), the server immediately re-seeds
   * a fresh empty placeholder of that type - this hook does not know about it until the next full
   * config fetch, which is fine since the UI already filters empty placeholder groups out (see
   * `src/utils/helpers/groups.ts`).
   */
  const deleteGroup = async (groupId: string): Promise<void> => {
    await apiUser.deleteGroup(groupId);
    dispatch(removeGroup(groupId));

    notifyExtensionWithoutBlocking();
  };

  return {
    updateGroupItems,
    updateFlagConfig,
    saveNewGroup,
    updateGroup,
    deleteGroup,
  };
};
